import React, { useCallback, useState } from 'react'

import { useDragToPan } from '../hooks/useDragToPan'
import { useImageZoom } from '../hooks/useImageZoom'

import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { IncidentNoteVideoPlayer } from '../../add-note/components/IncidentNoteVideoPlayer'
import { IncidentNotesMediaType } from '../types/types'
import { MediaPreviewerControls } from './MediaPreviewerControls'
import { ZoomableImage } from './ZoomableImage'

interface MediaPreviewerModalIProps {
  onClose: () => void
  isOpen: boolean
  file: IncidentNotesMediaType
}

const getModalBodyStyles = (isFull: boolean) => ({
  padding: isFull ? '10px' : '20px',
  maxWidth: isFull ? '100%' : '900px',
  maxHeight: isFull ? '100%' : '900px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

export const MediaPreviewerModal = ({
  file,
  onClose,
  isOpen,
}: MediaPreviewerModalIProps) => {
  const { mimeType, url, name } = file
  const [modalSize, setModalSize] = useState('3xl' as '3xl' | 'full')

  const mediaType = {
    isImage: mimeType.includes('image'),
    isVideo: mimeType.includes('video'),
    isPdf: mimeType.includes('pdf'),
  }

  const {
    zoomLevel,
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
    isZoomed,
  } = useImageZoom(isOpen)

  const { containerRef, isDragging, handlers } = useDragToPan(isZoomed)

  const handleToggleModalSize = useCallback(() => {
    const newSize = modalSize === '3xl' ? 'full' : '3xl'
    setModalSize(newSize)
    if (newSize === '3xl') {
      resetZoom()
    }
  }, [modalSize, resetZoom])

  const renderMediaContent = () => {
    if (mediaType.isVideo) {
      return <IncidentNoteVideoPlayer videoUrl={url} />
    }

    if (mediaType.isImage) {
      return (
        <ZoomableImage
          containerRef={containerRef}
          isDragging={isDragging}
          modalSize={modalSize}
          name={name}
          onToggleModalSize={handleToggleModalSize}
          url={url}
          zoomLevel={zoomLevel}
          {...handlers}
        />
      )
    }

    if (mediaType.isPdf) {
      return (
        <embed height={800} src={url} type='application/pdf' width='100%' />
      )
    }

    return null
  }

  return (
    <Modal
      closeOnOverlayClick
      isOpen={isOpen}
      onClose={onClose}
      size={modalSize}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize='16px'>{name}</ModalHeader>
        <ModalCloseButton />

        <MediaPreviewerControls
          modalSize={modalSize}
          onToggleModalSize={handleToggleModalSize}
          zoomActions={{
            onReset: resetZoom,
            onZoomIn: zoomIn,
            onZoomOut: zoomOut,
          }}
          zoomState={{
            canZoomIn,
            canZoomOut,
            isZoomed,
            shouldShowControls: mediaType.isImage && modalSize === 'full',
          }}
        />

        <ModalBody sx={getModalBodyStyles(modalSize === 'full')}>
          {renderMediaContent()}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
